import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: #f5f5f5;
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ToolbarCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

const GridContainer = styled.div`
  flex: 1;
  padding: 24px;
  overflow: hidden;
`;

function StockInfo() {
  const { group_code, project_code } = useParams();

  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemMaster, setItemMaster] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  
  // ✅ 스케줄링 옵션 상태
  const [schedulingOption, setSchedulingOption] = useState({ status: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    singleClickEdit: true,
  };

  useEffect(() => {
    fetchItemMaster();
    fetchStockInfo();
    fetchSchedulingOption(); // ✅ 추가
  }, [group_code, project_code]);

  useEffect(() => {
    const itemCodes = itemMaster.map((item) => item.item_code);

    setColumnDefs([
      {
        headerName: "선택",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 80,
        pinned: "left",
      },
      {
        headerName: "제품 코드",
        field: "item_code",
        width: 150,
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: itemCodes,
        },
      },
      {
        headerName: "제품명",
        field: "item_name",
        width: 250,
        editable: false,
        cellStyle: { backgroundColor: "#f5f5f5" },
      },
      {
        headerName: "요구 날짜",
        field: "required_date",
        width: 150,
        editable: true,
        cellEditor: "agDateStringCellEditor",
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("YYYY-MM-DD");
        },
      },
      {
        headerName: "요구 수량",
        field: "required_quantity",
        width: 130,
        editable: true,
        type: "numericColumn",
        valueParser: (params) => {
          const value = parseInt(params.newValue);
          return isNaN(value) ? 0 : value;
        },
      },
      {
        headerName: "업데이트 시점",
        field: "update_date",
        width: 180,
        editable: false,
        cellStyle: { backgroundColor: "#f5f5f5" },
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
        },
      },
    ]);
  }, [itemMaster]);

  // ✅ 스케줄링 옵션 조회
  const fetchSchedulingOption = async () => {
    try {
      const response = await axios.get(
        `${TPC_URL}/api/tpc/getTestSchedulingOption?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data) {
        setSchedulingOption(response.data);
      }
    } catch (error) {
      console.error("Error fetching scheduling option:", error);
    }
  };

  // ✅ 스케줄링 옵션 토글 다이얼로그 열기
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  // ✅ 스케줄링 옵션 토글 확인
  const handleConfirmToggle = async () => {
    const newStatus = schedulingOption.status === 0 ? 1 : 0;
    
    try {
      setLoading(true);
      
      await axios.put(`${TPC_URL}/api/tpc/updateTestSchedulingOption`, {
        group_code,
        project_code,
        status: newStatus,
      });

      setSnackbar({
        open: true,
        message: `${newStatus === 0 ? "엑셀 데이터" : "임시 재고 데이터"} 사용으로 변경되었습니다.`,
        severity: "success",
      });

      // ✅ 업데이트 후 다시 조회
      await fetchSchedulingOption();
      
    } catch (error) {
      console.error("Error updating scheduling option:", error);
      setSnackbar({
        open: true,
        message: "스케줄링 옵션 변경 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  // ✅ 다이얼로그 닫기
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const fetchItemMaster = async () => {
    try {
      const response = await axios.get(
        `${TPC_URL}/api/stock/getItemMaster?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data && Array.isArray(response.data)) {
        setItemMaster(response.data);
      }
    } catch (error) {
      console.error("Error fetching item master:", error);
    }
  };

  const fetchStockInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${TPC_URL}/api/stock/getStockInfo?group_code=${group_code}&project_code=${project_code}`
      );

      if (response.data && Array.isArray(response.data)) {
        setRowData(response.data);
      }
    } catch (error) {
      console.error("Error fetching stock info:", error);
      setSnackbar({
        open: true,
        message: "데이터를 불러오는 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const onSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selected = gridApi.getSelectedRows();
      setSelectedRows(selected);
    }
  }, [gridApi]);

  const onCellEditingStopped = useCallback(
    (params) => {
      if (params.column.getColId() === "item_code") {
        const selectedItem = itemMaster.find(
          (item) => item.item_code === params.value
        );

        if (selectedItem) {
          const updatedData = rowData.map((row, index) => {
            if (index === params.node.rowIndex) {
              return {
                ...row,
                item_id: selectedItem.item_id,
                item_code: selectedItem.item_code,
                item_name: selectedItem.item_name,
              };
            }
            return row;
          });

          setRowData(updatedData);
        }
      }
    },
    [itemMaster, rowData]
  );

  const handleAddRow = () => {
    const newRow = {
      info_id: null,
      item_id: null,
      item_code: "",
      item_name: "",
      required_date: dayjs().format("YYYY-MM-DD"),
      required_quantity: 0,
      update_date: null,
      isNew: true,
    };

    setRowData((prev) => [newRow, ...prev]);

    setTimeout(() => {
      if (gridApi) {
        gridApi.setFocusedCell(0, "item_code");
        gridApi.startEditingCell({
          rowIndex: 0,
          colKey: "item_code",
        });
      }
    }, 100);
  };

  const handleSave = async () => {
    if (!gridApi) return;

    const allRows = [];
    gridApi.forEachNode((node) => allRows.push(node.data));

    const newRows = allRows.filter((row) => row.isNew);
    const updatedRows = allRows.filter((row) => !row.isNew && row.info_id);

    const invalidRows = allRows.filter(
      (row) => !row.item_id || !row.required_date || row.required_quantity < 0
    );

    if (invalidRows.length > 0) {
      setSnackbar({
        open: true,
        message: "제품 코드, 요구 날짜, 요구 수량을 확인해주세요.",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);

      if (newRows.length > 0) {
        await axios.post(`${TPC_URL}/api/stock/insertStockInfo`, {
          group_code,
          project_code,
          data: newRows.map((row) => ({
            item_id: row.item_id,
            required_date: row.required_date,
            required_quantity: row.required_quantity,
          })),
        });
      }

      if (updatedRows.length > 0) {
        await axios.put(`${TPC_URL}/api/stock/updateStockInfo`, {
          group_code,
          project_code,
          data: updatedRows.map((row) => ({
            info_id: row.info_id,
            item_id: row.item_id,
            required_date: row.required_date,
            required_quantity: row.required_quantity,
          })),
        });
      }

      setSnackbar({
        open: true,
        message: "저장되었습니다.",
        severity: "success",
      });

      await fetchStockInfo();
    } catch (error) {
      console.error("Error saving stock info:", error);
      setSnackbar({
        open: true,
        message: "저장 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "삭제할 행을 선택해주세요.",
        severity: "warning",
      });
      return;
    }

    const idsToDelete = selectedRows
      .filter((row) => row.info_id)
      .map((row) => row.info_id);

    if (idsToDelete.length === 0) {
      setRowData((prev) =>
        prev.filter((row) => !selectedRows.some((sel) => sel === row))
      );
      setSnackbar({
        open: true,
        message: "선택된 행이 삭제되었습니다.",
        severity: "info",
      });
      return;
    }

    try {
      setLoading(true);

      await axios.delete(
        `${TPC_URL}/api/stock/deleteStockInfo?group_code=${group_code}&project_code=${project_code}&ids=${idsToDelete.join(
          ","
        )}`
      );

      setSnackbar({
        open: true,
        message: `${idsToDelete.length}개 행이 삭제되었습니다.`,
        severity: "success",
      });

      await fetchStockInfo();
    } catch (error) {
      console.error("Error deleting stock info:", error);
      setSnackbar({
        open: true,
        message: "삭제 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              재고 정보 관리
            </Typography>
            <Typography variant="body2" color="text.secondary">
              총 {rowData.length}개
            </Typography>
          </ToolbarLeft>
          
          {/* ✅ 중간 영역 추가 */}
          <ToolbarCenter>
            <Chip
              label={schedulingOption.status === 0 ? "엑셀 데이터 사용중" : "임시 재고 데이터 사용중"}
              color={schedulingOption.status === 0 ? "default" : "primary"}
              onClick={handleOpenDialog}
              sx={{ 
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "14px",
                padding: "20px 12px",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            />
          </ToolbarCenter>
          
          <ToolbarRight>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchStockInfo}
              disabled={loading}
            >
              새로고침
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={loading || selectedRows.length === 0}
            >
              삭제
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              disabled={loading}
            >
              추가
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
            >
              저장
            </Button>
          </ToolbarRight>
        </ToolbarContainer>

        <GridContainer>
          <Paper
            className="ag-theme-alpine"
            sx={{
              width: "100%",
              height: "100%",
            }}
          >
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={onGridReady}
              onSelectionChanged={onSelectionChanged}
              onCellEditingStopped={onCellEditingStopped}
              animateRows={true}
              enableCellChangeFlash={true}
              stopEditingWhenCellsLoseFocus={true}
            />
          </Paper>
        </GridContainer>
      </FrameContainer>

      {/* ✅ 확인 다이얼로그 추가 */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
      >
        <DialogTitle>데이터 소스 변경</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {schedulingOption.status === 0
              ? "임시 재고 데이터를 사용하도록 변경하시겠습니까?"
              : "엑셀 데이터를 사용하도록 변경하시겠습니까?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            취소
          </Button>
          <Button onClick={handleConfirmToggle} color="primary" variant="contained">
            확인
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default StockInfo;
